import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Toast } from 'flowbite-react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((toast) => {
    const id = Math.random().toString(36).slice(2);
    const ttl = toast.ttlMs ?? 4000;
    const item = { id, type: toast.type || 'info', message: toast.message || '' };
    setToasts((prev) => [...prev, item]);
    setTimeout(() => removeToast(id), ttl);
    return id;
  }, [removeToast]);

  const value = useMemo(() => ({ addToast, removeToast }), [addToast, removeToast]);

  const colorMap = {
    success: { bg: 'bg-green-100', text: 'text-green-600', icon: '✓' },
    error: { bg: 'bg-red-100', text: 'text-red-600', icon: '!' },
    warning: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '!' },
    info: { bg: 'bg-blue-100', text: 'text-blue-600', icon: 'i' },
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed left-4 top-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => {
          const c = colorMap[t.type] || colorMap.info;
          return (
            <Toast key={t.id}>
              <div className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${c.bg} ${c.text}`}>
                {c.icon}
              </div>
              <div className="ml-3 text-sm font-normal">{t.message}</div>
              <Toast.Toggle onDismiss={() => removeToast(t.id)} />
            </Toast>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};